/**
 * Client Form Page
 *
 * Mobile-first form for creating and editing clients.
 */

import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { ArrowLeft } from 'lucide-react'

import { useClient, useCreateClient, useUpdateClient } from '@/hooks/useClients'
import { parseApiError } from '@/lib/utils'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Spinner } from '@/components/ui/spinner'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ActivityCodeSelect } from '@/components/clients/ActivityCodeSelect'
import {
  IdentificationType,
  IDENTIFICATION_PATTERNS,
  IDENTIFICATION_ERROR_KEYS,
} from '@/types'

const clientSchema = z.object({
  name: z.string().min(1, t('validation.required')),
  type_identification: z.string().min(1, t('validation.required')),
  identification: z.string().min(1, t('validation.required')),
  email: z.string().email(t('validation.invalidEmail')).optional().or(z.literal('')),
  phone: z.string().optional(),
  celphone: z.string().optional(),
  address: z.string().optional(),
  province: z.string().optional(),
  canton: z.string().optional(),
  credit_limit: z.coerce.number().min(0, t('validation.mustBePositive')),
  payment_days: z.coerce.number().int().min(0, t('validation.mustBePositive')),
  credit_blocked: z.boolean(),
  activity_code: z.string().optional(),
  activity_code_name: z.string().optional(),
}).refine((data) => {
  // Validate identification length/format per Costa Rica type (Hacienda v4.4)
  const pattern = IDENTIFICATION_PATTERNS[data.type_identification as IdentificationType]
  return pattern ? pattern.test(data.identification) : true
}, (data) => ({
  message: t(
    IDENTIFICATION_ERROR_KEYS[data.type_identification as IdentificationType] ??
      'validation.invalidIdentification'
  ),
  path: ['identification'],
}))

type ClientFormValues = z.infer<typeof clientSchema>

const identificationTypes = [
  { value: '01', label: t('idTypes.type01') },
  { value: '02', label: t('idTypes.type02') },
  { value: '03', label: t('idTypes.type03') },
  { value: '04', label: t('idTypes.type04') },
  { value: '05', label: t('idTypes.type05') },
]

export function ClientFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditing = !!id

  const { data: client, isLoading: isLoadingClient } = useClient(Number(id))
  const createClient = useCreateClient()
  const updateClient = useUpdateClient()

  const form = useForm<ClientFormValues>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      name: '',
      type_identification: '01',
      identification: '',
      email: '',
      phone: '',
      celphone: '',
      address: '',
      province: '',
      canton: '',
      credit_limit: 0,
      payment_days: 30,
      credit_blocked: false,
      activity_code: '',
      activity_code_name: '',
    },
  })

  // Load client data when editing
  useEffect(() => {
    if (client) {
      form.reset({
        name: client.name,
        type_identification: client.type_identification,
        identification: client.identification,
        email: client.email || '',
        phone: client.phone || '',
        celphone: client.celphone || '',
        address: client.address || '',
        province: client.province || '',
        canton: client.canton || '',
        credit_limit: client.credit_limit,
        payment_days: client.payment_days,
        credit_blocked: client.credit_blocked ?? false,
        activity_code: client.activity_code || '',
        activity_code_name: client.activity_code_name || '',
      })
    }
  }, [client, form])

  const onSubmit = async (data: ClientFormValues) => {
    try {
      const cleanData = {
        ...data,
        email: data.email || undefined,
        phone: data.phone || undefined,
        celphone: data.celphone || undefined,
        address: data.address || undefined,
        province: data.province || undefined,
        canton: data.canton || undefined,
        // null (no undefined): si se manda undefined, axios omite el campo y el
        // backend con exclude_unset deja el valor viejo — limpiar nunca guardaría.
        activity_code: data.activity_code || null,
        activity_code_name: data.activity_code_name || null,
      }

      if (isEditing) {
        await updateClient.mutateAsync({ id: Number(id), data: cleanData })
      } else {
        await createClient.mutateAsync(cleanData)
      }
      navigate('/clients')
    } catch (error) {
      form.setError('root', { message: parseApiError(error) })
    }
  }

  const isLoading = createClient.isPending || updateClient.isPending

  if (isEditing && isLoadingClient) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl md:text-2xl font-bold">
          {isEditing ? t('clients.editClient') : t('clients.newClient')}
        </h1>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {form.formState.errors.root && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
              {form.formState.errors.root.message}
            </div>
          )}

          {/* Basic Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('clients.basicInfo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('clients.name')} *</FormLabel>
                    <FormControl>
                      <Input {...field} className="h-12 text-base" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="type_identification"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('clients.identificationType')} *</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="h-12">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {identificationTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="identification"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('clients.identification')} *</FormLabel>
                      <FormControl>
                        <Input {...field} className="h-12 text-base" inputMode="numeric" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('clients.contact')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('clients.email')}</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" className="h-12 text-base" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('clients.phone')}</FormLabel>
                      <FormControl>
                        <Input {...field} type="tel" className="h-12 text-base" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="celphone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('clients.celphone')}</FormLabel>
                      <FormControl>
                        <Input {...field} type="tel" className="h-12 text-base" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('clients.address')}</FormLabel>
                    <FormControl>
                      <Input {...field} className="h-12 text-base" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Credit */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('clients.credit')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="credit_limit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('clients.creditLimit')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          className="h-12 text-base"
                          inputMode="decimal"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="payment_days"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('clients.paymentDays')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          className="h-12 text-base"
                          inputMode="numeric"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="credit_blocked"
                render={({ field }) => (
                  <FormItem className="mt-4 flex items-center justify-between gap-4 rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>{t('clients.creditBlocked')}</FormLabel>
                      <p className="text-xs text-muted-foreground">{t('clients.creditBlockedHint')}</p>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Actividad Económica */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('activities.economicActivity')}</CardTitle>
            </CardHeader>
            <CardContent>
              <ActivityCodeSelect
                identification={form.watch('identification')}
                value={form.watch('activity_code') || ''}
                valueName={form.watch('activity_code_name') || ''}
                onChange={(code, name) => {
                  form.setValue('activity_code', code)
                  form.setValue('activity_code_name', name)
                }}
              />
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="h-12 sm:flex-1"
              onClick={() => navigate(-1)}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" className="h-12 sm:flex-1" disabled={isLoading}>
              {isLoading && <Spinner size="sm" className="mr-2" />}
              {isEditing ? t('common.update') : t('clients.createClient')}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
